import { APP_BASE } from '@/lib/api-base';

/**
 * A link stored by the backend (a notification's `link_path`, say) → the full
 * in-app path to `navigate()` to.
 *
 * The router has no `basename`: every route is registered with the prefix
 * already in it (`/app/...` locally, `/ukv-payroll/app/...` behind the
 * production proxy — see lib/api-base). So the target must carry APP_BASE,
 * whatever form the producer stored:
 *
 *   /fnf?tab=exits                  → {APP_BASE}/fnf?tab=exits
 *   /app/fnf?tab=exits              → {APP_BASE}/fnf?tab=exits
 *   /ukv-payroll/app/fnf?tab=exits  → {APP_BASE}/fnf?tab=exits  (on that host)
 *   /approvals                      → {APP_BASE}/approvals      (not "/app" + "rovals")
 *
 * Anything that is not a plain single-slash path — `//host`, `http://…`, or
 * backslash tricks browsers turn into `//host` (React Router 6.x lets those
 * through, CVE-2026-53669) — lands on the app root instead of leaving it.
 */
export function toAppPath(link: string | null | undefined, appBase: string = APP_BASE): string {
  const root = `${appBase}/`;
  if (!link) return root;
  const p = link.trim().replace(/\\/g, '/');
  if (!/^\/(?!\/)/.test(p)) return root;
  const rest = stripSegment(p, appBase) ?? stripSegment(p, '/app') ?? p;
  return `${appBase}${rest.startsWith('/') ? rest : `/${rest}`}`;
}

/** `p` without a leading `base` path segment, or null when it has none. */
function stripSegment(p: string, base: string): string | null {
  if (!base || base === '/') return null;
  if (p === base) return '/';
  const next = p.charAt(base.length);
  if (p.startsWith(base) && (next === '/' || next === '?' || next === '#')) {
    return p.slice(base.length) || '/';
  }
  return null;
}
